import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  Card,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { addDays, format } from "date-fns";
import React from "react";
import StatusBadge from "../../components/StatusBadge/StatusBadge";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { AxiosService } from "../../services/axios/axiosService";
import { STATUS_CONFIG, getStatusDisplayLabel, normalizeStatusKey } from "../../constant";
import { alpha } from "@mui/material/styles";
import { formatDateTimeShortIst, parseApiDate } from "../../utils/dateFormatUtils";
const safeStr = (v) => (typeof v === "string" ? v : v == null ? "" : String(v));

/** Plain text for display (handles simple HTML from APIs). */
const htmlToPlain = (raw) => {
  const s = safeStr(raw).trim();
  if (!s) return "";
  if (typeof document === "undefined") return s.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const div = document.createElement("div");
  div.innerHTML = s.replace(/<br\s*\/?>/gi, "\n");
  return (div.textContent || "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

const JD_PREVIEW_CHARS = 280;

const truncateJobDescriptionPreview = (text, maxChars) => {
  const s = safeStr(text).trim();
  if (s.length <= maxChars) return s;
  const slice = s.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(" ");
  const end = lastSpace > maxChars * 0.55 ? lastSpace : maxChars;
  return `${slice.slice(0, end).trimEnd()}…`;
};

/** API scheduled date — shared by hardcoded milestone + dynamic Scheduled row. */
const getInterviewScheduledDateFromCandidate = (candidate) =>
  candidate?.interviewScheduledDate ??
  candidate?.InterviewScheduledDate ??
  candidate?.scheduledDate ??
  candidate?.ScheduledDate ??
  null;

/** Show "Interview Scheduled" before dynamic status for every API status except Scheduled. */
const shouldShowInterviewScheduledMilestone = (statusLabel) => {
  const key = normalizeStatusKey(statusLabel);
  if (!key) return false;
  return key !== "scheduled";
};

const getUpdatedDateFromCandidate = (candidate) =>
  candidate?.updatedDate ??
  candidate?.UpdatedDate ??
  candidate?.interviewUpdatedDate ??
  candidate?.InterviewUpdatedDate ??
  null;

/** Link expires 2 days after interviewScheduledDate. */
const getLinkExpiredDateFromCandidate = (candidate) => {
  const scheduled = parseApiDate(getInterviewScheduledDateFromCandidate(candidate));
  if (!scheduled) return null;
  return addDays(scheduled, 2);
};

/** Pick the timeline date for the dynamic interview-status row from API status. */
const getInterviewStatusTimelineDate = (statusLabel, candidate) => {
  const key = normalizeStatusKey(statusLabel);
  if (key === "scheduled") return getInterviewScheduledDateFromCandidate(candidate);
  if (key === "completed") {
    return (
      candidate?.interviewCompletedDate ??
      candidate?.InterviewCompletedDate ??
      null
    );
  }
  if (key === "linkexpired") return getLinkExpiredDateFromCandidate(candidate);
  return getUpdatedDateFromCandidate(candidate);
};

const formatTimelineIstTime = (value) => {
  if (!parseApiDate(value)) return "";
  const formatted = formatDateTimeShortIst(value);
  return formatted === "-" ? "" : formatted;
};

const getTimelineStatusDotColor = (statusLabel) => {
  const normalized = normalizeStatusKey(statusLabel);
  if (!normalized) return "#16a34a";

  const configEntry = Object.entries(STATUS_CONFIG).find(([key, cfg]) => {
    return normalizeStatusKey(key) === normalized || normalizeStatusKey(cfg?.label) === normalized;
  });
  return configEntry?.[1]?.color || "#16a34a";
};

const getInitials = (fullName) => {
  const s = safeStr(fullName).trim();
  if (!s) return "C";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? "C";
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return `${a}${b}`.toUpperCase();
};

const toDateOrNull = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isFinite(d.getTime()) ? d : null;
};

const formatDateShort = (value) => {
  const d = toDateOrNull(value);
  return d ? format(d, "MMM dd, yyyy") : safeStr(value) || "-";
};

const formatDateTimeShort = (value) => {
  const d = toDateOrNull(value);
  return d ? format(d, "MMM dd, yyyy • hh:mm a") : safeStr(value) || "-";
};

/** Timeline right column: hide when no parseable date (avoid a lone "-"). */
const formatTimelineTimeOrEmpty = (value) => {
  const d = toDateOrNull(value);
  return d ? format(d, "MMM dd, yyyy • hh:mm a") : "";
};

const CandidateProfileDetailDialog = ({ open, onClose, candidate }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [profile, setProfile] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState("");
  const [jdExpanded, setJdExpanded] = React.useState(false);

  React.useEffect(() => {
    setJdExpanded(false);
  }, [open, candidate?.id]);

  React.useEffect(() => {
    if (!open) {
      setProfile(null);
      setDetailLoading(false);
      setDetailError("");
      return;
    }
    if (!candidate) {
      setProfile(null);
      return;
    }

    setProfile(candidate);
    const id = candidate?.id ?? candidate?.candidateId;
    if (id == null || id === "") {
      setDetailLoading(false);
      setDetailError("");
      return;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError("");

    (async () => {
      try {
        const res = await AxiosService.get(`/api/candidate/${id}`);
        if (cancelled) return;
        setProfile((prev) => ({ ...(prev || candidate), ...(res && typeof res === "object" ? res : {}) }));
      } catch (e) {
        if (cancelled) return;
        setDetailError(safeStr(e?.message) || "Could not load candidate details.");
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, candidate]);

  const c = profile ?? candidate;

  const name = c?.candidateName ?? c?.fullName ?? "-";
  const email = c?.candidateEmail ?? c?.email ?? "-";
  const phone = c?.candidatePhone ?? c?.contactNumber ?? c?.Contact_Number ?? "-";
  const locationText = c?.location ?? c?.Location ?? "-";
  const experienceText =
    c?.experienceYears != null
      ? `${c.experienceYears} years`
      : c?.experience ?? c?.OverallExperience ?? "-";
  const appliedDate = c?.appliedDate ?? c?.createdAt ?? "-";
  const jobTitle = c?.jobTitle || c?.position || "-";
  const jobId = c?.jobId ?? c?.positionId ?? c?.jobPostId ?? c?.JobOpeningId ?? "-";
  const source = c?.zohoCandidateId ? "Zoho Recruit (Auto)" : "By Scout";
  const interviewStatusRaw =
    c?.interviewStatus ?? c?.InterviewStatus ?? c?.status ?? c?.Status ?? c?.interviewScheduleStatus;
  const interviewStatusLabel = safeStr(interviewStatusRaw).trim();
  const interviewStatusDisplayLabel = getStatusDisplayLabel(interviewStatusLabel);
  const hasInterviewStatus = interviewStatusLabel.length > 0;
  const interviewStatusForBadge = interviewStatusLabel || "";
  const interviewStatusDotColor = getTimelineStatusDotColor(interviewStatusLabel);
  const interviewScheduledDate = getInterviewScheduledDateFromCandidate(c);
  const interviewScheduledTimelineTime = formatTimelineIstTime(interviewScheduledDate);
  const isScheduledStatus = normalizeStatusKey(interviewStatusLabel) === "scheduled";
  const showInterviewScheduledMilestone = shouldShowInterviewScheduledMilestone(interviewStatusLabel);
  const interviewStatusTimelineTime = isScheduledStatus
    ? interviewScheduledTimelineTime
    : formatTimelineIstTime(getInterviewStatusTimelineDate(interviewStatusLabel, c));
  const resumeUrl = c?.resumeUrl ?? c?.resumeURL ?? c?.resume ?? c?.resume_link ?? "";
  const hasResume = !!safeStr(resumeUrl).trim();

  const jdFallback =
    "We are looking for a strong candidate with experience in modern frontend development and cloud-ready systems.";
  const jdRaw = safeStr(c?.jobDescription).trim();
  const jdSource = jdRaw || jdFallback;
  const jdPlain = htmlToPlain(jdSource) || jdFallback;
  const jdNeedsToggle = jdPlain.length > JD_PREVIEW_CHARS;
  const jdShown = jdExpanded || !jdNeedsToggle ? jdPlain : truncateJobDescriptionPreview(jdPlain, JD_PREVIEW_CHARS);

  const onDownloadResume = () => {
    const u = safeStr(resumeUrl).trim();
    if (!u) return;
    window.open(u, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog
      open={!!open}
      onClose={(_e, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose?.();
      }}
      fullWidth
      fullScreen={isSmallScreen}
      maxWidth="md"
      disableEscapeKeyDown
      PaperProps={{ sx: { borderRadius: isSmallScreen ? 0 : 3, overflow: "hidden" } }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(15, 23, 42, 0.55)", backdropFilter: "blur(2px)" },
        },
      }}
    >
      <DialogTitle sx={{ px: 2.5, py: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="subtitle1" fontWeight={800}>
          Candidate Profile
        </Typography>
        <Button aria-label="Close" variant="outlined" color="secondary" size="small" sx={{ textTransform: "none", fontWeight: 600, minWidth: "auto" }}   onClick={() => onClose?.()}>
        Close<CloseRoundedIcon />
         
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.25 } }}>
        {!candidate ? (
          <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3 }}>
            <Typography fontWeight={900}>No candidate selected</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Please close and open a profile from the table.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ maxWidth: 1240, mx: "auto", position: "relative" }}>
            {detailLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.background.paper, 0.72),
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {!!detailError && (
              <Typography variant="caption" color="error" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                {detailError}
              </Typography>
            )}
           
              <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 2.25 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        bgcolor: "rgba(37,99,235,0.10)",
                        border: `1px solid ${theme.palette.divider}`,
                        display: "grid",
                        placeItems: "center",
                        fontWeight: 900,
                        color: theme.palette.primary.main,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(name)}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                        <Typography variant="h6" sx={{ fontWeight: 850 }}>
                          {name}
                        </Typography>
                        <StatusBadge
                          status={interviewStatusForBadge}
                          sx={{ fontWeight: 750 }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
                        {/* <Stack direction="row" spacing={0.5} alignItems="center">
                          <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", fontWeight:600 }} />
                          <Typography variant="caption" color="text.secondary" >
                            {safeStr(jobTitle) ? `${safeStr(jobTitle)}` : "No job title"}
                          </Typography>
                        </Stack> */}
                        {/* <Stack direction="row" spacing={0.5} alignItems="center">
                          <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", fontWeight:'bold' }} />
                          <Typography variant="caption" color="text.secondary" >
                            {safeStr(experienceText) ? `${safeStr(experienceText)} years` : "No experience"} 
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PlaceOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary">
                            {safeStr(locationText) || "No location"}
                          </Typography>
                        </Stack> */}
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary" >
                            Applied : {formatDateShort(appliedDate) || "No applied date"}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>

                  <Button
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={onDownloadResume}
                    disabled={!safeStr(resumeUrl).trim()}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      width: { xs: "100%", md: "auto" },
                      alignSelf: { xs: "stretch", md: "center" },
                      flexShrink: 0,
                    }}
                  >
                    Download resume
                  </Button>
                </Stack>

             

               
              </Paper>
          

            <Stack spacing={3} sx={{ mt: 3 }}>
              {/* CANDIDATE INFORMATION (CONTACT STRIP) */}
              <Card elevation={0} sx={{  borderRadius: 3 }} variant="outlined">
                <Box sx={{ px: 2, py: 1.5, bgcolor: theme.palette.background.paper }}>
                  <Typography variant="subtitle2" fontWeight={900}>
                    Candidate Information
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2, py: 2 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        // Email + Location need more room; Phone + Experience stay narrow.
                        md: "minmax(0, 2.5fr) minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1.1fr)",
                      },
                      gap: 1.5,
                    }}
                  >
                    {[
                      { label: "Email", value: email, wide: true },
                      { label: "Phone", value: phone, wide: false },
                      { label: "Location", value: locationText, wide: true },
                      { label: "Overall Experience", value: experienceText, wide: false },
                    ].map((c) => (
                      <Box key={c.label} sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          //color="text.secondary"
                          fontWeight={500}
                          display="block"
                         
                          sx={{ mb: 0.5, letterSpacing: 0.4 }}
                        >
                          {c.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            //fontWeight: 500,
                            overflowWrap: c.wide ? "anywhere" : "normal",
                            wordBreak: c.wide ? "break-word" : "normal",
                            whiteSpace: c.wide ? "normal" : "nowrap",
                            textOverflow: c.wide ? "clip" : "ellipsis",
                            overflow: c.wide ? "visible" : "hidden",
                          }}
                          title={c.wide ? undefined : safeStr(c.value)}
                        >
                          {safeStr(c.value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Card>

             
              {/* ACTIVITY TIMELINE (SEPARATE CARD) */}
              <Card elevation={0} sx={{  borderRadius: 3 }} variant="outlined">
                <Box sx={{ px: 2, py: 1.5 }}>                 
                <Typography variant="body2" fontWeight={900}> Activity Timeline</Typography>                 
                </Box>
                <Divider />
                <Box sx={{ px: 2, py: 2 }}>
                  {[
                    {
                      dot: "#f59e0b",
                      title: "Profile Created",
                      inlineMeta: source,
                      subtitle: "",
                      time: formatDateTimeShort(c?.createdAt ?? appliedDate),
                    },
                    ...(hasResume
                      ? [
                          {
                            dot: "#a532d5",
                            title: "Resume Uploaded",
                            subtitle: "",
                            time: formatDateTimeShort(c?.createdAt ?? appliedDate),
                          },
                        ]
                      : []),
                    ...(hasInterviewStatus
                      ? [
                          ...(showInterviewScheduledMilestone
                            ? [
                                {
                                  key: "timeline-interview-scheduled",
                                  dot: "#2563eb",
                                  title: "Scheduled",
                                  subtitle: "",
                                  time: interviewScheduledTimelineTime,
                                },
                              ]
                            : []),
                          {
                            key: "timeline-interview-status",
                            dot: interviewStatusDotColor,
                            title: "",
                            subtitle: interviewStatusDisplayLabel,
                            time: interviewStatusTimelineTime,
                          },
                        ]
                      : []),
                  ].map((e, idx, arr) => (
                    <Box
                      key={e.key ?? e.title}
                      sx={{ display: "flex", gap: 2, position: "relative", pb: idx === arr.length - 1 ? 0 : 2 }}
                    >
                      <Box sx={{ width: 16, display: "flex", justifyContent: "center" }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: e.dot, mt: 0.6 }} />
                        {idx < arr.length - 1 && (
                          <Box sx={{ position: "absolute", left: 7, top: 18, bottom: 0, width: 2, bgcolor: theme.palette.divider }} />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {!!safeStr(e.title).trim() && (
                          <Stack
                            direction="row"
                            spacing={0.75}
                            alignItems="baseline"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Typography variant="body2" fontWeight={500} component="span">
                              {e.title}
                            </Typography>
                            {!!safeStr(e.inlineMeta).trim() && (
                              <Typography variant="caption" color="text.secondary" component="span">
                                {e.inlineMeta}
                              </Typography>
                            )}
                          </Stack>
                        )}
                        {!!safeStr(e.subtitle).trim() && (
                          <Typography
                            variant={safeStr(e.title).trim() ? "caption" : "body2"}
                            color={safeStr(e.title).trim() ? "text.secondary" : "text.primary"}
                            component="div"
                            sx={{
                              mt: safeStr(e.title).trim() ? 0.25 : 0,
                              display: "block",
                              fontWeight: safeStr(e.title).trim() ? 400 : 500,
                            }}
                          >
                            {e.subtitle}
                          </Typography>
                        )}
                      </Box>

                      {!!safeStr(e.time).trim() && (
                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ whiteSpace: "nowrap", pt: 0.25 }}>
                          {e.time}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Card>

               {/* JOB APPLICATION DETAILS (SEPARATE CARD) */}
               <Card elevation={0} sx={{  borderRadius: 3 }} variant="outlined">
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={900}>
                    Job Application Details
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2, py: 2 }}>
                  <Grid container spacing={15}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption"  fontWeight={500}>
                        Position Applied For
                      </Typography>
                      <Typography variant="body2"  sx={{ mt: 0.5 }} color="text.secondary">
                        {safeStr(jobTitle)}
                      </Typography>
                    </Grid>
                    {/* <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" >
                        Job ID
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5 }}>
                        {safeStr(jobId) || "No job ID"}
                      </Typography>
                    </Grid> */}
                  </Grid>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption"  fontWeight={500}>
                      Job Description
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                    >
                      {jdShown}
                    </Typography>
                    {jdNeedsToggle && (
                      <Button
                        type="button"
                        variant="text"
                        size="small"
                        onClick={() => setJdExpanded((v) => !v)}
                        sx={{
                          mt: 0.25,
                          p: 0,
                          minWidth: 0,
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                        }}
                      >
                        {jdExpanded ? "see less..." : "see more..."}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Card>

            </Stack>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CandidateProfileDetailDialog;

